import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import getContactDisplay from '@afosto/utils/getContactDisplay';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import Property from '../Property';
import Link from '@material-ui/core/Link';
import translations from './translations';

const BASE_URL = process.env.REACT_APP_BASE_APP_URL;
const CONTACTS_APP_ORIGIN = BASE_URL.replace('{app}', 'contacts');

const ContactContainer = ({ title, children }) => {
  const intl = useIntl();

  return (
    <Card>
      <CardHeader
        title={title || intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent>{children}</CardContent>
    </Card>
  );
};

ContactContainer.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
};

ContactContainer.defaultProps = {
  title: undefined,
};

const ContactInformation = ({ contact, organisation, isLoading, ContainerElement }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['rel:contacts:read']);

  if (!hasPermissions()) {
    return (
      <ContactContainer>
        <UnauthorizedComponent showActions={false} />
      </ContactContainer>
    );
  }

  const fullName = getContactDisplay(contact || {});

  return (
    <ContainerElement>
      <LoadingWrapper isLoading={isLoading}>
        {contact && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.personalInformation)}
                value={
                  <>
                    <Link variant="body2" href={`${CONTACTS_APP_ORIGIN}/contacts/${contact.id}`}>
                      {fullName}
                    </Link>
                    {fullName !== contact.email && (
                      <Typography variant="body2">{contact.email}</Typography>
                    )}
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.phoneNumber)}
                value={contact?.phone_numbers?.primary?.number}
              />
            </Grid>
          </Grid>
        )}

        {!contact && (
          <Typography variant="body2">{intl.formatMessage(translations.emptyMessage)}</Typography>
        )}

        {organisation && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.organisation)}
                value={
                  <>
                    <Typography variant="body2">
                      <Link
                        variant="body2"
                        href={`${CONTACTS_APP_ORIGIN}/organisations/${organisation.id}`}
                      >
                        {organisation.name}
                      </Link>
                    </Typography>
                    <Typography variant="body2">{organisation.coc_number}</Typography>
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Property
                label={intl.formatMessage(translations.phoneNumber)}
                value={organisation?.phone_numbers?.primary?.number}
              />

              {organisation?.vat_number && (
                <Box mt={2}>
                  <Property
                    label={intl.formatMessage(translations.vatNumber)}
                    value={organisation.vat_number}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </LoadingWrapper>
    </ContainerElement>
  );
};

ContactInformation.propTypes = {
  contact: PropTypes.object,
  organisation: PropTypes.object,
  isLoading: PropTypes.bool,
  ContainerElement: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.string]),
};

ContactInformation.defaultProps = {
  contact: null,
  organisation: null,
  isLoading: true,
  ContainerElement: ContactContainer,
};

export default ContactInformation;
